import { useEffect } from 'react';
import { Router, useRouter } from 'next/router';
import { experimentedPages } from 'experiment.config';

import { publicEnv } from '~/features/environment/public';

/**
 * This is a hacky solution to a problem that we have with Cloudflare & Next.js.
 * The Cloudflare Worker does not run on client navigations.
 * This is a global solution to check if the user is performing a client navigation to a legal or experimented page.
 * By manually navigating the user to the same page, we force the Cloudflare Worker to run.
 * Throwing an error is apparently the only solution to stop the client navigation in Nextjs.
 */
export function useCancelClientNavigation() {
  const router = useRouter();

  useEffect(() => {
    const handleRouteChange = (nextPathname: string) => {
      const currentPath = router.asPath;

      if (nextPathname.includes('coupon=')) {
        cancelClientNavigationAndManuallyRedirect(nextPathname);
      }

      // Note: we want to allow client-side transitions only in checkout.
      if (nextPathname.startsWith('/checkout/') && !currentPath.startsWith('/checkout/')) {
        cancelClientNavigationAndManuallyRedirect(nextPathname);
      }

      if (nextPathname.startsWith('/catalog') && !currentPath.startsWith('/catalog')) {
        cancelClientNavigationAndManuallyRedirect(nextPathname);
      }

      if (nextPathname.startsWith('/legal/')) {
        cancelClientNavigationAndManuallyRedirect(nextPathname);
      }

      if (publicEnv.NEXT_PUBLIC_ENVIRONMENT_NAME !== 'test') {
        for (const experimentedPage of experimentedPages) {
          if (
            experimentedPage.pathRegex.test(nextPathname) &&
            !(
              experimentedPage.experimentKey === 'ab_plp_redesign_2025' &&
              currentPath.startsWith('/catalog') &&
              nextPathname.startsWith('/catalog')
            )
          ) {
            cancelClientNavigationAndManuallyRedirect(nextPathname);
          }
        }
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router]);
}

function cancelClientNavigationAndManuallyRedirect(pathname: string) {
  window.location.href = pathname;
  // Ref: https://github.com/vercel/next.js/discussions/32231
  Router.events.emit('routeChangeError');
  if (process.env.NODE_ENV !== 'development') {
    throw new Error('Abort Route Change');
  }
}
